import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { Routes } from '../../config/routes';
import { LegFiJwtService } from '../auth/legfi-jwt.service';
import { JwtLegFiClaims } from '../auth/jwt-legfi-claims.model';
import { ApplicationHttpClient } from '../../components/shared/http/application-http-client';
import { FailedRequestBufferService } from '../../components/shared/http/failed-request-buffer.service';
import { BreadcrumbBufferService } from './breadcrumb-buffer.service';
import { PageContextService, OnScreenEntity } from './page-context.service';
import { RedactionService } from './redaction.service';

/** Severity vocabulary — must match the backend enum exactly. */
export type BugSeverity = 'blocking' | 'major' | 'minor';

/** Scope vocabulary — must match the backend enum exactly. */
export type BugScope = 'single_user' | 'single_account' | 'widespread';

/** A failed request as carried in telemetry (camelCase, matches the contract). */
export interface BugTelemetryFailedRequest {
    method: string;
    url: string;
    status: number;
    traceId: string | null;
    timestamp: string;
}

export interface BugReportContext {
    url: string;
    route: string;
    pageComponent: string;
    viewport: { w: number; h: number };
    userAgent: string;
    entitiesOnScreen: OnScreenEntity[];
    featureFlags: string[];
}

export interface BugReportTelemetry {
    failedRequests: BugTelemetryFailedRequest[];
    traceId: string | null;
}

/** Fields the user fills in on the drawer (everything else is auto-assembled). */
export interface BugReportFormInput {
    summary: string;
    expected: string;
    actual: string;
    reproSteps: string[];
    severity: BugSeverity;
    scope: BugScope;
}

/** The complete, redacted payload POSTed to the backend. */
export interface BugReportPayload {
    summary: string;
    expected: string;
    actual: string;
    reproSteps: string[];
    severity: BugSeverity;
    scope: BugScope;
    context: BugReportContext;
    telemetry: BugReportTelemetry;
    dataSnapshot: OnScreenEntity[];
}

/** 201 response shape from the submit endpoint. */
export interface BugReportSubmitResponse {
    bugReportId: number;
    renderedIssue: string;
}

/** A single super-admin activity-feed row (camelCase from the API). */
export interface BugReportActivity {
    id: number;
    bugReportId: number;
    organizationId: number;
    actorUserId: number | null;
    actorMembershipId: number | null;
    actorName: string | null;
    actorEmail: string | null;
    actorOrganizationId: number | null;
    event: string;
    detail: string | null;
    createdAt: string;
}

/** Filters accepted by the activity feed. */
export interface BugReportActivityFilters {
    organizationId?: number;
    bugReportId?: number;
    event?: string;
    page?: number;
    perPage?: number;
}

/** Paginated activity-feed response. */
export interface BugReportActivityResponse {
    data: BugReportActivity[];
    currentPage: number;
    perPage: number;
    total: number;
    lastPage: number;
}

/** Status vocabulary — must match the backend enum exactly. */
export type BugStatus =
    | 'submitted'
    | 'triaged'
    | 'routed'
    | 'resolving'
    | 'pr_open'
    | 'in_review'
    | 'changes_requested'
    | 'resolved'
    | 'needs_human'
    | 'failed'
    | 'wont_fix';

/**
 * Compact triage brief carried on each list row so a collapsed one-line summary
 * (resolution kind + confidence) can render without fetching the full brief.
 * Null until the report is triaged.
 */
export interface TriageListSummary {
    resolutionKind: string | null;
    /** Word bucket ('high'/'medium'/'low') or a numeric score — the agent emits either. */
    confidence: number | string | null;
}

/** A single row in the super-admin bug-report list (camelCase from the API). */
export interface BugReportListItem {
    id: number;
    summary: string;
    severity: BugSeverity | string;
    scope: BugScope | string;
    status: BugStatus | string;
    organizationId: number | null;
    submittedByUserId: number | null;
    /** Routed Jira ticket key/url (null until the bot creates + stamps the ticket). */
    jiraIssueKey: string | null;
    jiraIssueUrl: string | null;
    /** Compact triage summary for the collapsed row (null until triaged). */
    triageSummary: TriageListSummary | null;
    createdAt: string;
}

/** Filters accepted by the bug-report list endpoint. */
export interface BugReportListFilters {
    organizationId?: number;
    status?: string;
    page?: number;
    perPage?: number;
}

/** Paginated bug-report list response. */
export interface BugReportListResponse {
    data: BugReportListItem[];
    currentPage: number;
    perPage: number;
    total: number;
    lastPage: number;
}

/** A file the triage agent suspects is involved in the bug. */
export interface TriageSuspectFile {
    path: string;
    why: string | null;
}

/** Risk assessment carried on the triage brief. */
export interface TriageRisk {
    reversible: boolean | null;
    blastRadius: string | null;
}

/**
 * Proposed data fix carried on the triage brief for `data_fix` resolutions.
 * Usually null for code/config fixes. The API emits an object; older payloads
 * may emit a plain string, so the type tolerates both.
 */
export interface TriageDataFixProposal {
    records: string | null;
    beforeAfter: string | null;
    reversibleScript: string | null;
    verifyQuery: string | null;
}

/**
 * The AI triage brief attached to a report once triaged (nullable until then).
 * The API may emit camelCase or snake_case — `detail()` normalizes to camelCase.
 */
export interface TriageBrief {
    resolutionKind: string | null;
    /** Word bucket ('high'/'medium'/'low') or a numeric score — the agent emits either. */
    confidence: number | string | null;
    likelyArea: string | null;
    suspectedFiles: TriageSuspectFile[];
    duplicateOf: number | null;
    risk: TriageRisk | null;
    verification: string | null;
    needsHumanReason: string | null;
    dataFixProposal: TriageDataFixProposal | string | null;
    renderedIssue: string | null;
}

/** The full super-admin bug-report detail (camelCase from the API). */
export interface BugReportDetail {
    id: number;
    summary: string;
    expected: string | null;
    actual: string | null;
    reproSteps: string[];
    severity: BugSeverity | string;
    scope: BugScope | string;
    status: BugStatus | string;
    organizationId: number | null;
    context: BugReportContext | null;
    telemetry: BugReportTelemetry | null;
    dataSnapshot: OnScreenEntity[];
    agentBrief: TriageBrief | null;
    renderedIssue: string | null;
    jiraIssueKey: string | null;
    jiraIssueUrl: string | null;
    createdAt: string;
}

/**
 * Submits bug reports and reads the super-admin activity feed. Mirrors
 * `ActivityLogService`'s shape over `ApplicationHttpClient` — JWT null-check →
 * `redirectAndThrow401Observable()`, then `map` the camelCase response.
 *
 * The submit path assembles the full payload from the capture layer
 * (breadcrumbs, failed-request buffer, page context, JWT flags + window/route)
 * and runs it through `RedactionService` before it leaves the browser.
 */
@Injectable({ providedIn: 'root' })
export class BugReportService {
    constructor(
        private _http: ApplicationHttpClient,
        private _failedRequests: FailedRequestBufferService,
        private _breadcrumbs: BreadcrumbBufferService,
        private _pageContext: PageContextService,
        private _redaction: RedactionService,
    ) {}

    /**
     * Assemble the report payload from the four user fields + severity/scope and
     * the auto-captured context/telemetry. The result is fully redacted and
     * ready to preview or submit.
     */
    public assemblePayload(form: BugReportFormInput): BugReportPayload {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        const entities = this._pageContext.getEntitiesOnScreen();
        const failed = this._failedRequests.getAll();
        // Newest failed request's traceId (or null) correlates the report to a log.
        const newestTraceId = failed.length > 0 ? failed[failed.length - 1].traceId : null;

        const raw: BugReportPayload = {
            summary: form.summary,
            expected: form.expected,
            actual: form.actual,
            reproSteps: form.reproSteps,
            severity: form.severity,
            scope: form.scope,
            context: {
                url: window.location.href,
                route: window.location.pathname,
                pageComponent: '',
                viewport: { w: window.innerWidth, h: window.innerHeight },
                userAgent: window.navigator.userAgent,
                entitiesOnScreen: entities,
                featureFlags: jwt?.ff ?? [],
            },
            telemetry: {
                failedRequests: failed.map((f) => ({
                    method: f.method,
                    url: f.url,
                    status: f.status,
                    traceId: f.traceId,
                    timestamp: f.timestamp,
                })),
                traceId: newestTraceId,
            },
            dataSnapshot: entities,
        };

        return this._redaction.redact(raw);
    }

    /**
     * POST a bug report. Mirrors `ActivityLogService.getActivities`: JWT
     * null-check → `redirectAndThrow401Observable()` before issuing the request.
     */
    public submit(payload: BugReportPayload): Observable<BugReportSubmitResponse> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.BugReportModule.Submit(jwt.orgId);

        return this._http
            .post<BugReportSubmitResponse>(url, this._http.serializeObject(payload as unknown as { [key: string]: any }))
            .pipe(map((response: any) => ({
                bugReportId: response.bugReportId,
                renderedIssue: response.renderedIssue,
            })));
    }

    /** GET the super-admin bug-report activity feed (paginated + filterable). */
    public getActivity(filters?: BugReportActivityFilters): Observable<BugReportActivityResponse> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.BugReportModule.Activity()
            + this._http.queryStringFromObject(filters ?? {});

        return this._http.get(url).pipe(map((response: any) => ({
            data: Array.isArray(response.data) ? response.data : [],
            currentPage: response.currentPage ?? 1,
            perPage: response.perPage ?? 25,
            total: response.total ?? 0,
            lastPage: response.lastPage ?? 1,
        })));
    }

    /** GET the super-admin bug-report list (paginated + filterable). */
    public list(filters?: BugReportListFilters): Observable<BugReportListResponse> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.BugReportModule.List()
            + this._http.queryStringFromObject(filters ?? {});

        return this._http.get(url).pipe(map((response: any) => ({
            data: Array.isArray(response.data) ? response.data.map((row: any) => this._mapListItem(row)) : [],
            currentPage: response.currentPage ?? response.current_page ?? 1,
            perPage: response.perPage ?? response.per_page ?? 25,
            total: response.total ?? 0,
            lastPage: response.lastPage ?? response.last_page ?? 1,
        })));
    }

    /** GET a single super-admin bug-report detail by id. */
    public detail(id: number): Observable<BugReportDetail> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.BugReportModule.Detail(id);

        return this._http.get(url).pipe(map((response: any) => this._mapDetail(response)));
    }

    /** Normalize a list row, tolerating camelCase or snake_case keys. */
    private _mapListItem(row: any): BugReportListItem {
        return {
            id: row.id,
            summary: row.summary,
            severity: row.severity,
            scope: row.scope,
            status: row.status,
            organizationId: row.organizationId ?? row.organization_id ?? null,
            submittedByUserId: row.submittedByUserId ?? row.submitted_by_user_id ?? null,
            jiraIssueKey: row.jiraIssueKey ?? row.jira_issue_key ?? null,
            jiraIssueUrl: row.jiraIssueUrl ?? row.jira_issue_url ?? null,
            triageSummary: this._mapTriageSummary(row.triageSummary ?? row.triage_summary ?? null),
            createdAt: row.createdAt ?? row.created_at,
        };
    }

    /** Normalize the compact list triage summary, tolerating camelCase or snake_case keys. */
    private _mapTriageSummary(raw: any): TriageListSummary | null {
        if (raw === null || raw === undefined) {
            return null;
        }

        return {
            resolutionKind: raw.resolutionKind ?? raw.resolution_kind ?? null,
            confidence: raw.confidence ?? null,
        };
    }

    /** Normalize the detail payload, tolerating camelCase or snake_case keys. */
    private _mapDetail(response: any): BugReportDetail {
        const brief = response.agentBrief ?? response.agent_brief ?? null;

        return {
            id: response.id,
            summary: response.summary,
            expected: response.expected ?? null,
            actual: response.actual ?? null,
            reproSteps: response.reproSteps ?? response.repro_steps ?? [],
            severity: response.severity,
            scope: response.scope,
            status: response.status,
            organizationId: response.organizationId ?? response.organization_id ?? null,
            context: response.context ?? null,
            telemetry: response.telemetry ?? null,
            dataSnapshot: response.dataSnapshot ?? response.data_snapshot ?? [],
            agentBrief: brief ? this._mapBrief(brief) : null,
            renderedIssue: response.renderedIssue ?? response.rendered_issue ?? null,
            jiraIssueKey: response.jiraIssueKey ?? response.jira_issue_key ?? null,
            jiraIssueUrl: response.jiraIssueUrl ?? response.jira_issue_url ?? null,
            createdAt: response.createdAt ?? response.created_at,
        };
    }

    /** Normalize the AI triage brief, tolerating camelCase or snake_case keys. */
    private _mapBrief(brief: any): TriageBrief {
        const rawFiles = brief.suspectedFiles ?? brief.suspected_files ?? [];
        const rawRisk = brief.risk ?? null;

        return {
            resolutionKind: brief.resolutionKind ?? brief.resolution_kind ?? null,
            confidence: brief.confidence ?? null,
            likelyArea: brief.likelyArea ?? brief.likely_area ?? null,
            suspectedFiles: Array.isArray(rawFiles)
                ? rawFiles.map((f: any) => ({ path: f.path, why: f.why ?? null }))
                : [],
            duplicateOf: brief.duplicateOf ?? brief.duplicate_of ?? null,
            risk: rawRisk
                ? {
                    reversible: rawRisk.reversible ?? null,
                    blastRadius: rawRisk.blastRadius ?? rawRisk.blast_radius ?? null,
                }
                : null,
            verification: brief.verification ?? null,
            needsHumanReason: brief.needsHumanReason ?? brief.needs_human_reason ?? null,
            dataFixProposal: this._mapDataFixProposal(brief.dataFixProposal ?? brief.data_fix_proposal ?? null),
            renderedIssue: brief.renderedIssue ?? brief.rendered_issue ?? null,
        };
    }

    /**
     * Normalize the data-fix proposal. Usually null (code/config fixes). When
     * present it is an object whose fields may arrive camelCase or snake_case;
     * a plain string is passed through untouched for backwards compatibility.
     */
    private _mapDataFixProposal(raw: any): TriageDataFixProposal | string | null {
        if (raw === null || raw === undefined) {
            return null;
        }
        if (typeof raw === 'string') {
            return raw;
        }

        return {
            records: raw.records ?? null,
            beforeAfter: raw.beforeAfter ?? raw.before_after ?? null,
            reversibleScript: raw.reversibleScript ?? raw.reversible_script ?? null,
            verifyQuery: raw.verifyQuery ?? raw.verify_query ?? null,
        };
    }
}
